import {jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment} from "react/jsx-runtime"; function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import * as ScrollArea from '@radix-ui/react-scroll-area'
import graphql from 'babel-plugin-relay/macro'
import {Fragment, useEffect, useMemo, useState} from 'react'
import {
  commitLocalUpdate,
  fetchQuery,

  usePreloadedQuery,
  useRefetchableFragment
} from 'react-relay'
import {Redirect} from 'react-router'
import {Link} from 'react-router-dom'





import useAtmosphere from '../../hooks/useAtmosphere'
import {useDebouncedSearch} from '../../hooks/useDebouncedSearch'
import useRouter from '../../hooks/useRouter'
import useSearchFilter from '../../hooks/useSearchFilter'
import logoMarkPurple from '../../styles/theme/images/brand/mark-color.svg'
import {cn} from '../../ui/cn'
import SendClientSideEvent from '../../utils/SendClientSideEvent'
import IconLabel from '../IconLabel'
import LoadingComponent from '../LoadingComponent/LoadingComponent'
import ActivityGrid from './ActivityGrid'
import ActivityLibraryEmptyState from './ActivityLibraryEmptyState'
import {

  CATEGORY_ID_TO_NAME,
  CATEGORY_THEMES,

  CUSTOM_CATEGORY_ID,
  QUICK_START_CATEGORY_ID
} from './Categories'
import SearchBar from './SearchBar'

graphql`
  fragment ActivityLibrary_templateSearchDocument on MeetingTemplate {
    team {
      name
    }
    name
    type
    category
    scope
    ... on PokerTemplate {
      dimensions {
        name
        description
        selectedScale {
          name
        }
      }
    }
    ... on ReflectTemplate {
      prompts {
        question
        description
      }
      subCategories
    }
  }
`

graphql`
  fragment ActivityLibrary_template on MeetingTemplate {
    id
    teamId
    team {
      name
    }
    name
    type
    category
    illustrationUrl
    isRecommended
    scope
    ...ActivityLibrary_templateSearchDocument @relay(mask: false)
    ...ActivityCard_template
    ...ActivityLibraryCardDescription_template
  }
`

const templateSearchFragment = graphql`
  fragment ActivityLibraryTemplateSearch_query on Query
  @argumentDefinitions(search: {type: "String!"})
  @refetchable(queryName: "ActivityLibraryTemplateSearchRefetchQuery") {
    viewer {
      templateSearch(search: $search) {
        ...ActivityLibrary_template @relay(mask: false)
      }
    }
  }
`

const templateSearchQuery = graphql`
  query ActivityLibraryTemplateSearchQuery($search: String!) {
    ...ActivityLibraryTemplateSearch_query @arguments(search: $search)
  }
`

const query = graphql`
  query ActivityLibraryQuery {
    ...ActivityLibraryTemplateSearch_query @arguments(search: "")
    viewer {
      ...ActivityGrid_user
      favoriteTemplates {
        ...ActivityLibrary_template @relay(mask: false)
      }
      availableTemplates(first: 2000) @connection(key: "ActivityLibrary_availableTemplates") {
        edges {
          node {
            ...ActivityLibrary_template @relay(mask: false)
          }
        }
      }
    }
  }
`





const CATEGORY_KEYWORDS = {
  strategy: ['okrs', 'goalsetting', 'goal-setting', 'planning'],
  estimation: ['sprint'],
  premortem: ['kickoff']
}

// Generate a string of concatenated keywords that we can match against for each template.
const getTemplateDocumentValue = (
  template
) =>
  [
    template.name,
    template.category,
    template.type,
    template.team.name,
    _nullishCoalesce(CATEGORY_KEYWORDS[template.category ], () => ( [])),
    _nullishCoalesce(_optionalChain([template, 'access', _ => _.dimensions, 'optionalAccess', _2 => _2.flatMap, 'call', _3 => _3((dimension) => [
      dimension.name,
      dimension.description,
      dimension.selectedScale.name
    ])]), () => ( [])),
    _nullishCoalesce(_optionalChain([template, 'access', _4 => _4.prompts, 'optionalAccess', _5 => _5.flatMap, 'call', _6 => _6((prompt) => [prompt.question, prompt.description])]), () => ( []))
  ]
    .flat()
    .join('-')

const CategoryIDToColorClass = Object.fromEntries(
  Object.entries(CATEGORY_THEMES).map(([key, value]) => {
    return [key, value.primary]
  })
) 



const MAX_PER_SUBCATEGORY = 6
const subCategoryMapping = {
  popular: 'Popular templates',
  recentlyUsed: 'You used these recently',
  recentlyUsedInOrg: 'Others in your organization are using',
  neverTried: 'Try these activities',
  getStarted: 'Activities to get you started',
  other: 'Other activities'
} 

const mapRetroSubCategories = (templates) => {
  const subCategoryTemplates = Object.fromEntries(
    Object.keys(subCategoryMapping).map((subCategory) => {
      return [
        subCategory,
        templates
          .filter((template) => _optionalChain([template, 'access', _7 => _7.subCategories, 'optionalAccess', _8 => _8.includes, 'call', _9 => _9(subCategory)]))
          .slice(0, MAX_PER_SUBCATEGORY) 
      ]
    })
  )
  //just in case there were values already
  subCategoryTemplates.other = []
  subCategoryTemplates.other = templates.filter(
    (template) =>
      !Object.values(subCategoryTemplates)
        .flat()
        .find((catTemplate) => catTemplate.id === template.id)
  )

  return Object.fromEntries(
    Object.entries(subCategoryTemplates).map(([key, value]) => {
      return [subCategoryMapping[key ], value]
    })
  )
}

const mapTeamCategories = (templates) => {
  // list public templates last
  const publicTemplates = [] 
  const mapped = templates.reduce(
    (acc, template) => {
      const {team, scope} = template
      if (scope === 'PUBLIC') {
        publicTemplates.push(template)
      } else {
        const {name} = team
        if (!acc[name]) {
          acc[name] = []
        }
        acc[name].push(template)
      }
      return acc
    },
    {} 
  )

  mapped['Parabol'] = publicTemplates
  return mapped
}

export const ActivityLibrary = (props) => {
  const atmosphere = useAtmosphere()
  const {queryRef} = props
  const data = usePreloadedQuery(query, queryRef)
  const {viewer} = data
  const {availableTemplates} = viewer

  const [isSearching, setIsSearching] = useState(true)
  const [templateSearch, refetchTemplateSearch] = useRefetchableFragment


(templateSearchFragment, data)

  const setSearch = (value) => {
    commitLocalUpdate(atmosphere, (store) => {
      const viewer = store.getRoot().getLinkedRecord('viewer')
      if (!viewer) return
      viewer.setValue(value, 'activityLibrarySearch')
    })
  }

  const templates = useMemo(() => {
    const templatesMap = availableTemplates.edges.map((edge) => edge.node)
    return templatesMap
  }, [availableTemplates])

  const availableCategoryIds = Object.keys(CATEGORY_ID_TO_NAME)

  const {
    query: searchQuery,
    filteredItems: filteredTemplates,
    onQueryChange,
    resetQuery
  } = useSearchFilter(templates, getTemplateDocumentValue)
  const {debouncedSearch: debouncedSearchQuery, dirty} = useDebouncedSearch(searchQuery)
  const showLoading = dirty || isSearching

  useEffect(() => {
    if (debouncedSearchQuery) {
      setIsSearching(true)
      // Avoid suspense while refreshing the search results, see
      // https://relay.dev/docs/guided-tour/refetching/refetching-fragments-with-different-data/#if-you-need-to-avoid-suspense
      fetchQuery(atmosphere, templateSearchQuery, {
        search: debouncedSearchQuery
      }).subscribe({
        complete: () => {
          refetchTemplateSearch({search: debouncedSearchQuery}, {fetchPolicy: 'store-only'})
          setIsSearching(false)
        },
        error: () => {
          setIsSearching(false)
        }
      })
      SendClientSideEvent(atmosphere, 'Activity Library Searched', {
        debouncedSearchQuery
      })
    } else {
      refetchTemplateSearch({search: ''}, {fetchPolicy: 'store-only'})
      setIsSearching(false)
    }
  }, [debouncedSearchQuery])

  const {match} = useRouter()
  const {
    params: {categoryId}
  } = match

  const templatesToRender = useMemo(() => {
    if (searchQuery.length > 0) {
      // If there's a search query, combine the filtered templates with the search results
      const searchResults = templateSearch.viewer.templateSearch
      const doubleMatches = searchResults.filter((searchResult) =>
        filteredTemplates.find((template) => template.id === searchResult.id)
      )

      return [
        ...doubleMatches,
        ...filteredTemplates.filter(
          (template) => !doubleMatches.find((doubleMatch) => doubleMatch.id === template.id)
        ),
        ...searchResults.filter(
          (searchResult) => !doubleMatches.find((doubleMatch) => doubleMatch.id === searchResult.id)
        )
      ]
    }
    if (categoryId === 'favorite') {
      return viewer.favoriteTemplates
    }

    return filteredTemplates.filter((template) =>
      categoryId === QUICK_START_CATEGORY_ID
        ? template.isRecommended
        : categoryId === CUSTOM_CATEGORY_ID
          ? template.scope !== 'PUBLIC'
          : template.category === categoryId
    )
  }, [searchQuery, filteredTemplates, templateSearch, categoryId])

  const sectionedTemplates = useMemo(() => {
    // Show the teams on search as well, because you can search by team name
    if (categoryId === CUSTOM_CATEGORY_ID || searchQuery.length > 0) {
      return mapTeamCategories(templatesToRender)
    }

    if (searchQuery.length > 0) {
      return undefined
    }

    if (categoryId === 'retrospective') {
      return mapRetroSubCategories(templatesToRender)
    }
    if (categoryId === 'recommended') {
      return {[subCategoryMapping.getStarted]: [...templatesToRender]}
    }
    return undefined
  }, [categoryId, templatesToRender])

  if (!categoryId || !availableCategoryIds.includes(categoryId)) {
    return _jsx(Redirect, { to: `/activity-library/category/${QUICK_START_CATEGORY_ID}`,} )
  }

  return (
    _jsxs('div', { className: "flex h-full w-full flex-col bg-white"    , children: [
      _jsxs('div', { className: "mx-2 flex" , children: [
        _jsx('div', { className: "hidden items-center justify-start gap-x-2 px-4 lg:flex lg:basis-[15%]"      , children: 
          _jsx(Link, { title: "My Dashboard" , to: "/meetings", children: 
            _jsx(IconLabel, { icon: 'arrow_back', iconLarge: true,} )
          })
        })

        , _jsxs('div', { className: "mt-4 flex flex-1 flex-col items-center border-slate-300 border-b-[1px] border-b-solid pb-4 lg:mr-[15%]"         , children: [
          _jsxs('div', { className: "mx-auto flex w-full items-center justify-between gap-14 px-2 md:px-4"       , children: [
            _jsxs('div', { className: "flex items-center" , children: [
              _jsx(Link, { className: "mr-6 block lg:hidden"  , title: "My Dashboard" , to: "/meetings", children: 
                _jsx(IconLabel, { icon: 'arrow_back', iconLarge: true,} )
              })
              , _jsx('img', { className: "mr-3 w-8" , crossOrigin: "", alt: "Parabol", src: logoMarkPurple,} )
              , _jsx('div', { className: "hidden shrink-0 font-semibold text-lg md:block lg:text-xl"     , children: "Start Activity"

              })
            ]})
            , _jsx('div', { className: "hidden grow md:block"  , children: 
              _jsx(SearchBar, {
                searchQuery: searchQuery,
                onChange: (e) => {
                  onQueryChange(e)
                  setSearch(e.target.value)
                },}
              )
            })

            , _jsx(Link, {
              className: "rounded-full bg-sky-500 px-4 py-2 font-medium text-sm text-white hover:bg-sky-600"       ,
              to: `/activity-library/new-activity/${categoryId}`,
 children: "Create custom activity"

            })
          ]})
          , _jsx('div', { className: "mt-4 flex w-full md:hidden"   , children: 
            _jsx(SearchBar, {
              searchQuery: searchQuery,
              onChange: (e) => {
                onQueryChange(e)
                setSearch(e.target.value)
              },}
            )
          })
        ]})
      ]})

      , _jsxs(ScrollArea.Root, { className: "w-full", children: [
        _jsx(ScrollArea.Viewport, { className: "w-full", children: 
          _jsx('div', { className: "flex gap-2 px-4 pt-6 md:flex-wrap md:pb-4 lg:mx-[15%]"      , children: 
            (availableCategoryIds ).map(
              (category) => (
                _jsx(Link, {
                  className: cn(
                    'flex shrink-0 cursor-pointer items-center rounded-full px-4 text-slate-800 text-sm leading-9',
                    category === categoryId && searchQuery.length === 0
                      ? [
                          `${CategoryIDToColorClass[category]}`,
                          'font-semibold text-white focus:text-white'
                        ]
                      : 'border border-slate-300 bg-white'
                  ),
                  to: `/activity-library/category/${category}`,
                  onClick: () => resetQuery(),

                  style: {
                    color:
                      category === 'favorite'
                        ? category === categoryId && searchQuery.length === 0
                          ? 'white'
                          : 'red'
                        : undefined
                  },
 children: 
                  CATEGORY_ID_TO_NAME[category]
                }, category)
              )
            )
          })
        })
        , _jsx(ScrollArea.Scrollbar, { orientation: "horizontal", className: "hidden",} )
      ]})

      , _jsxs(ScrollArea.Root, { className: "h-full w-full overflow-hidden"  , children: [
        _jsxs(ScrollArea.Viewport, { className: "flex h-full flex-col lg:mx-[15%]"   , children: [
          templatesToRender.length === 0 && !showLoading ? (
            _jsx(ActivityLibraryEmptyState, {
              searchQuery: searchQuery,
              categoryId: categoryId ,}
            )
          ) : (
            _jsx(_Fragment, { children: 
              sectionedTemplates ? (
                _jsx(_Fragment, { children: 
                  Object.entries(sectionedTemplates).map(([subCategory, subCategoryTemplates]) =>
                    subCategoryTemplates.length > 0 ? (
                      _jsxs(Fragment, { children: [
                        subCategory && (
                          _jsx('div', { className: "mt-8 ml-4 font-bold text-slate-700 text-xl"    , children: 
                            subCategory
                          })
                        )
                        , _jsx('div', { className: "mt-1 grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(min(40%,256px),1fr))] gap-4 px-4 md:mt-4"      , children: 
                          _jsx(ActivityGrid, {
                            templates: subCategoryTemplates,
                            selectedCategory: categoryId,
                            viewerRef: viewer,}
                          )
                        })
                      ]}, subCategory)
                    ) : (
                      _jsx('div', { className: "p-4",} )
                    )
                  )
                })
              ) : (
                _jsx(_Fragment, { children: 
                  _jsx('div', { className: "grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(min(40%,256px),1fr))] gap-4 p-4"    , children: 
                    _jsx(ActivityGrid, {
                      templates: templatesToRender ,
                      selectedCategory: categoryId,
                      viewerRef: viewer,}
                    )
                  })
                })
              )
            })
          )
          , showLoading && _jsx(LoadingComponent, {} )
        ]})
        , _jsx(ScrollArea.Scrollbar, {
          orientation: "vertical",
          className: "flex h-full w-2.5 touch-none select-none border-l border-l-transparent p-[1px] transition-colors"        ,
 children: 
          _jsx(ScrollArea.Thumb, { className: `relative flex-1 rounded-full bg-slate-600`,} )
        })
      ]})
    ]})
  )
}
